<template>
  <div class="mt-3">
    <p class="tw-font-bold tw-mb-1">Foto's {{ label }}</p>
    <p v-if="isLoading" class="has-text-grey">Laden...</p>
    <p v-else-if="!photos.length" class="has-text-grey">Geen foto's</p>
    <div v-else class="tw-flex tw-flex-wrap tw-gap-1">
      <button
        v-for="(photo, i) in photos"
        :key="photo.url"
        type="button"
        class="tw-p-0 tw-border-0 tw-bg-transparent"
        style="cursor: pointer"
        :title="photo.label"
        @click="open(i)"
      >
        <img
          :src="photo.url"
          :alt="photo.label"
          class="tw-h-20 tw-w-20 tw-object-cover tw-rounded tw-border hover:tw-opacity-80"
          style="cursor: pointer"
        />
      </button>
    </div>

    <!-- viewer: the page stays where it is -->
    <div
      v-if="current"
      class="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-flex-col tw-items-center tw-justify-center tw-bg-black/80 tw-p-4"
      @click.self="close"
    >
      <div class="tw-flex tw-items-center tw-gap-4 tw-text-white tw-mb-2 tw-w-full tw-max-w-5xl">
        <span class="tw-font-bold tw-capitalize">{{ current.label }}</span>
        <span class="tw-text-gray-300">{{ index + 1 }} / {{ photos.length }}</span>
        <a :href="current.url" target="_blank" rel="noopener" class="tw-ml-auto tw-text-white tw-underline">
          Origineel openen
        </a>
        <button type="button" class="delete is-medium" title="Sluiten (Esc)" @click="close"></button>
      </div>
      <div class="tw-flex tw-items-center tw-gap-3 tw-w-full tw-max-w-5xl tw-justify-center" @click.self="close">
        <button
          v-if="photos.length > 1"
          type="button"
          class="button is-dark is-rounded"
          title="Vorige (←)"
          @click="step(-1)"
        >
          <i class="fas fa-chevron-left"></i>
        </button>
        <img
          :src="current.url"
          :alt="current.label"
          class="tw-max-h-[80vh] tw-max-w-full tw-object-contain tw-rounded tw-shadow-lg"
        />
        <button
          v-if="photos.length > 1"
          type="button"
          class="button is-dark is-rounded"
          title="Volgende (→)"
          @click="step(1)"
        >
          <i class="fas fa-chevron-right"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
// The photos the installer took on the current ticket (latest approved, else latest not cancelled) of an inspection or installation task
// (ticket.output.images in the task detail, as the task details show them). A click opens them in
// a viewer on the page: arrows or the arrow keys to browse, Esc or a click outside to close.
import { computed, ref, onBeforeUnmount } from 'vue'
import { useTaskDetail } from './composables/useTaskDetail'
import { currentTicket } from './currentTicket'

const props = defineProps({
  address: { type: String, required: true },
  taskType: { type: String, required: true },
  project: { type: String, default: null },
  label: { type: String, default: '' },
})

const { data: components, isInitialLoading: isLoading } = useTaskDetail(
  computed(() => props.address),
  computed(() => props.taskType),
  computed(() => props.project)
)

// documents can sit in foto fields too; everything else is shown as a photo
const isImage = (url) => !/\.(pdf|xlsx?|csv|docx?)(\?|$)/i.test(url)

const photos = computed(() => {
  const ticket = currentTicket(components.value?.tickets)?.ticket
  if (!ticket) return []
  // current tickets: output.images; older tickets: top-level "foto" fields (as the task details read them)
  const legacy = Object.entries(ticket)
    .filter(([field, value]) => field.toLowerCase().includes('foto') && typeof value === 'string')
    .map(([field, url]) => ({ type: field, url }))
  return [...(ticket.output?.images ?? []), ...legacy]
    .filter((image) => typeof image?.url === 'string' && image.url && isImage(image.url))
    .map((image) => ({ url: image.url, label: (image.type || '').replace(/_/g, ' ') }))
})

// viewer
const index = ref(null)
const current = computed(() => (index.value === null ? null : photos.value[index.value] ?? null))

const step = (delta) => {
  const n = photos.value.length
  if (!n || index.value === null) return
  index.value = (index.value + delta + n) % n
}

const onKey = (event) => {
  if (event.key === 'Escape') close()
  else if (event.key === 'ArrowLeft') step(-1)
  else if (event.key === 'ArrowRight') step(1)
}

function open(i) {
  index.value = i
  window.addEventListener('keydown', onKey)
}

function close() {
  index.value = null
  window.removeEventListener('keydown', onKey)
}

onBeforeUnmount(() => window.removeEventListener('keydown', onKey))
</script>
